// src/services/masjidService.js
// A resilient prayer-times service that always returns structure
// and builds Jumu'ah 1/2 even when APIs fail.

import axios from "axios";
import { DEFAULT_MASJID_ID } from "../config/apiConfig";
import { getToken, setAuthToken } from "./apiClient";
import { getConnectUmmahCredentials } from "./connectUmmahCredentials";

const LOCAL_API = process.env.REACT_APP_LOCAL_API_BASE || "http://localhost:5000/api";
const CU_BASE = "https://api.connectummah.com/api";
const PRAYER_API_TIMEOUT_MS = 30000;
const LOGIN_TIMEOUT_MS = 15000;
const MASJID_RETRY_DELAY_MS = 1200;

// ---- helpers ----
const clean = (s) => (s == null ? "" : String(s).trim());
const norm12 = (s) => {
  s = clean(s);
  if (!s) return "";
  // accept "7", "7:5", "07:05 PM", "19:05"
  const m12 = s.match(/^(\d{1,2})(?::(\d{1,2}))?\s*([AaPp][Mm])$/);
  if (m12) {
    let h = Math.min(12, Math.max(1, parseInt(m12[1], 10) || 12));
    const mm = String(Math.min(59, Math.max(0, parseInt(m12[2] ?? "0", 10)))).padStart(2, "0");
    return `${h}:${mm} ${m12[3].toUpperCase()}`;
  }
  const m24 = s.match(/^(\d{1,2})(?::(\d{1,2}))$/);
  if (m24) {
    let hh = Math.min(23, Math.max(0, parseInt(m24[1], 10) || 0));
    const mm = Math.min(59, Math.max(0, parseInt(m24[2], 10) || 0));
    const mer = hh >= 12 ? "PM" : "AM";
    const h12 = (hh % 12) || 12;
    return `${h12}:${String(mm).padStart(2, "0")} ${mer}`;
  }
  const mh = s.match(/^(\d{1,2})$/);
  if (mh) {
    const hh = parseInt(mh[1], 10);
    if (hh >= 0 && hh <= 23) {
      const mer = hh >= 12 ? "PM" : "AM";
      const h12 = (hh % 12) || 12;
      return `${h12}:00 ${mer}`;
    }
  }
  return s;
};

const delay = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

async function fetchMasjidData(masjidId, headers = {}, retries = 1) {
  let lastError;

  for (let attempt = 0; attempt <= retries; attempt += 1) {
    try {
      const { data } = await axios.get(`${CU_BASE}/Masjid/${masjidId}`, {
        timeout: PRAYER_API_TIMEOUT_MS,
        headers: {
          accept: "*/*",
          ...headers,
        },
      });
      return data;
    } catch (error) {
      lastError = error;
      if (attempt < retries) {
        await delay(MASJID_RETRY_DELAY_MS);
      }
    }
  }

  throw lastError;
}

function buildJummahArrayFromAnything(data = {}) {
  const directArray = data.jummahTimes || data.jumuahTimes;

  // helper to normalize first non-empty
  const first = (obj, keys) => {
    for (const k of keys) {
      const v = obj?.[k];
      if (v != null && String(v).trim() !== "") return v;
    }
    return null;
  };

  // 1) If backend already returns an array
  if (Array.isArray(directArray) && directArray.length) {
    return [...directArray]
      .sort((a, b) => (a.sequence ?? 0) - (b.sequence ?? 0))
      .map((j, idx) => {
        const startSrc =
          first(j, ["start", "startTime", "jummahStart", "lecture", "lectureTime", "dars", "darsTime", "bayan", "bayanTime"]) ??
          first(j, ["khutba_Time", "khutbah_Time", "khutba"]) ?? // fallback to khutbah if no explicit start
          "";

        return {
          sequence: (j.sequence ?? idx) || idx,
          // Use start field for the HomePage component
          start: norm12(startSrc),
          iqamah: norm12(j.iqama_Time || j.iqamah_Time || j.iqma_Time || j.salah_Time || j.prayer_Time || j.iqamah || j.salah || ""),
          // Keep original fields for compatibility
          khutba_Time: norm12(j.khutba_Time || j.khutbah_Time || j.khutba || ""),
          iqama_Time: norm12(j.iqama_Time || j.iqamah_Time || j.iqma_Time || j.salah_Time || j.prayer_Time || j.iqamah || j.salah || ""),
          lecture_Time: norm12(first(j, ["lecture", "lectureTime", "dars", "darsTime", "lecture_Time", "bayanTime"]) || ""),
        };
      });
  }

  // 2) Derive from iqamatimes/startTimes when array isn't provided
  const iq = data?.iqamatimes || data?.iqamahTimes || {};
  const st = data?.startTimes?.timings || data?.timings || data?.data?.timings || {};

  const j1Start =
    first(st, ["jumuah1", "Jumuah1", "Jumu'ah1", "jummah1", "Jummah1", "khutbah1", "Khutbah1", "khutba1", "Khutba1"]) ??
    first(iq, ["lecture1", "Lecture1", "dars1", "Dars1", "bayan1", "Bayan1", "jumuah1_khutba", "jumuah1_khutbah", "Jumuah1_Khutba", "Jumuah1_Khutbah", "jummah1_khutba", "jummah1_khutbah", "Jummah1_Khutba", "Jummah1_Khutbah"]) ??
    process.env.REACT_APP_JUMMAH1_KHUTBA;

  const j2Start =
    first(st, ["jumuah2", "Jumuah2", "Jumu'ah2", "jummah2", "Jummah2", "khutbah2", "Khutbah2", "khutba2", "Khutba2"]) ??
    first(iq, ["lecture2", "Lecture2", "dars2", "Dars2", "bayan2", "Bayan2", "jumuah2_khutba", "jumuah2_khutbah", "Jumuah2_Khutba", "Jumuah2_Khutbah", "jummah2_khutba", "jummah2_khutbah", "Jummah2_Khutba", "Jummah2_Khutbah"]) ??
    process.env.REACT_APP_JUMMAH2_KHUTBA;

  const j1 = {
    sequence: 1,
    start: norm12(j1Start || ""),
    iqamah: norm12(
      first(iq, ["jumuah1_iqamah", "Jumuah1_Iqamah", "jummah1_iqamah", "Jummah1_Iqamah", "jumuah1_salah", "Jumuah1_Salah", "jummah1_salah", "Jummah1_Salah", "iqamah1", "prayer1", "salah1", "iqama1"]) ??
      process.env.REACT_APP_JUMMAH1_IQAMAH
    ),
    // Keep original fields for compatibility
    khutba_Time: norm12(j1Start || ""),
    iqama_Time: norm12(
      first(iq, ["jumuah1_iqamah", "Jumuah1_Iqamah", "jummah1_iqamah", "Jummah1_Iqamah", "jumuah1_salah", "Jumuah1_Salah", "jummah1_salah", "Jummah1_Salah", "iqamah1", "prayer1", "salah1", "iqama1"]) ??
      process.env.REACT_APP_JUMMAH1_IQAMAH
    ),
    lecture_Time: norm12(
      first(iq, ["lecture1", "lecture_Time", "dars1", "Dars1", "bayan1", "Bayan1", "jumuah1_lecture", "Jumuah1_Lecture", "jummah1_lecture", "Jummah1_Lecture"]) ||
      ""
    ),
  };

  const j2 = {
    sequence: 2,
    start: norm12(j2Start || ""),
    iqamah: norm12(
      first(iq, ["jumuah2_iqamah", "Jumuah2_Iqamah", "jummah2_iqamah", "Jummah2_Iqamah", "jumuah2_salah", "Jumuah2_Salah", "jummah2_salah", "Jummah2_Salah", "iqamah2", "prayer2", "salah2", "iqama2"]) ??
      process.env.REACT_APP_JUMMAH2_IQAMAH
    ),
    // Keep original fields for compatibility
    khutba_Time: norm12(j2Start || ""),
    iqama_Time: norm12(
      first(iq, ["jumuah2_iqamah", "Jumuah2_Iqamah", "jummah2_iqamah", "Jummah2_Iqamah", "jumuah2_salah", "Jumuah2_Salah", "jummah2_salah", "Jummah2_Salah", "iqamah2", "prayer2", "salah2", "iqama2"]) ??
      process.env.REACT_APP_JUMMAH2_IQAMAH
    ),
    lecture_Time: norm12(
      first(iq, ["lecture1", "lecture_Time", "dars1", "Dars1", "bayan1", "Bayan1", "jumuah1_lecture", "Jumuah1_Lecture", "jummah1_lecture", "Jummah1_Lecture"]) ||
      ""
    ),
  };

  const out = [];
  if (j1.start || j1.iqamah) out.push(j1);
  if (j2.start || j2.iqamah) out.push(j2);

  // 3) ENV-only last fallback
  if (!out.length) {
    const e1k = norm12(process.env.REACT_APP_JUMMAH1_KHUTBA || "");
    const e1i = norm12(process.env.REACT_APP_JUMMAH1_IQAMAH || "");
    const e2k = norm12(process.env.REACT_APP_JUMMAH2_KHUTBA || "");
    const e2i = norm12(process.env.REACT_APP_JUMMAH2_IQAMAH || "");
    if (e1k || e1i) out.push({ sequence: 1, start: e1k, iqamah: e1i, khutba_Time: e1k, iqama_Time: e1i });
    if (e2k || e2i) out.push({ sequence: 2, start: e2k, iqamah: e2i, khutba_Time: e2k, iqama_Time: e2i });
  }

  return out;
}

function normalizeResponse(raw = {}) {
  // make sure shape exists so UI never breaks
  const startTimes = raw.startTimes?.timings
    ? raw.startTimes
    : { timings: raw.timings || raw.data?.timings || {} };

  const iqamatimes =
    raw.iqamatimes || raw.iqamahTimes || raw.IqamahTimes || raw.data?.iqamatimes || {};

  const jummahTimes = buildJummahArrayFromAnything(raw);
  const yearlyIqamaTimeDtos = Array.isArray(raw.yearlyIqamaTimeDtos)
    ? raw.yearlyIqamaTimeDtos
    : [];

  return { startTimes, iqamatimes, jummahTimes, yearlyIqamaTimeDtos };
}

// ---- main exported fn ----
export async function getPrayerTimes(masjidId = DEFAULT_MASJID_ID) {
  try {
    const { data } = await axios.get(`${LOCAL_API.replace(/\/+$/, "")}/prayer-times/${masjidId}`, {
      timeout: PRAYER_API_TIMEOUT_MS,
    });
    return normalizeResponse(data || {});
  } catch (localError) {
    console.warn("Local prayer-times API failed, trying direct ConnectUmmah API...", localError?.message || localError);
  }

  try {
    const currentToken = getToken();
    const data = await fetchMasjidData(
      masjidId,
      currentToken ? { Authorization: `Bearer ${currentToken}` } : {},
      currentToken ? 1 : 0
    );
    return normalizeResponse(data || {});
  } catch (publicError) {
    console.warn("Direct public prayer-times request failed.", publicError?.message || publicError);
  }

  const creds = getConnectUmmahCredentials();
  if (creds.username && creds.password) {
    try {
      const res = await axios.post(
        `${CU_BASE}/Auth/LoginMultiTenantWithTenantId`,
        {
          username: creds.username,
          password: creds.password,
          tenant_Id: creds.tenantId,
        },
        {
          timeout: LOGIN_TIMEOUT_MS,
          headers: { "Content-Type": "application/json", Accept: "*/*" },
        }
      );

      const token = res?.data?.token || "";
      if (token) {
        setAuthToken(token, false);
        const data = await fetchMasjidData(
          masjidId,
          { Authorization: `Bearer ${token}` },
          1
        );
        return normalizeResponse(data || {});
      }
    } catch (authError) {
      console.warn("Authenticated prayer-times request failed.", authError?.message || authError);
    }
  }

  const fallback = {
    startTimes: { timings: {} },
    iqamatimes: {},
    jummahTimes: {},
  };
  return normalizeResponse(fallback);
}
